import { Injectable } from '@angular/core';
import { BillingService, Contract, Currency, Frequency, WholesalePricingService } from '@galaxy/billing';
import { BehaviorSubject, Observable, combineLatest, throwError } from 'rxjs';
import { catchError, delay, map, retryWhen } from 'rxjs/operators';

import {
  BillingFrequency,
  PartnerSubscriptionService,
  SubscriptionTierInterface,
  SubscriptionTierUpdateType,
  UpdateSubscriptionTierStatus,
} from '@galaxy/partner';
import { AppConfigService } from '../../app-config.service';
import { UnifiedTOSService } from '../../core/tos-unified.service';
import { BillingSettingsService } from '../../shared-billing/settings/billing-settings.service';

export interface UpgradeTierData {
  partnerID?: string;
  tier?: SubscriptionTierInterface;
  subscriptionOnboardingPrice?: number;
  preSelectedFrequency?: Frequency;
  upgradeType?: SubscriptionTierUpdateType;
  monthlyAddonPrice?: number;
  yearlyAddonPrice?: number;
}

export interface DialogPricingPlan {
  name: string;
  caption?: string;
  monthlyPrice?: number;
  price?: number;
  currency?: string;
  frequency?: Frequency;
  totalCost?: number;
  onboardingPrice?: number;
}

export interface WaitForSubscriptionChangeResult {
  success: boolean;
  reason?: string;
}

@Injectable()
export class UpgradeAllSubscriptionModalService {
  billingInfoEditable = false;
  private purchasing$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  purchasing$: Observable<boolean> = this.purchasing$$.asObservable();
  contract$: Observable<Contract>;

  constructor(
    private billingService: BillingService,
    private billingSettingsService: BillingSettingsService,
    private appConfigService: AppConfigService,
    public tosService: UnifiedTOSService,
    private partnerSubscriptionService: PartnerSubscriptionService,
    private wholesalePricingService: WholesalePricingService,
  ) {
    this.billingInfoEditable = appConfigService.config.userCanAccessBilling;
  }

  public get shouldEnablePurchase(): Observable<boolean> {
    return combineLatest([
      this.purchasing$$,
      this.billingSettingsService.isMerchantConfigured,
      this.billingSettingsService.isPaymentDetailsConfigured,
      this.billingSettingsService.loading,
    ]).pipe(map(([purchasing, merchant, payment]) => !purchasing && merchant && payment));
  }

  public isCanadianCurrency(): Observable<boolean> {
    return this.contract$.pipe(
      map((contract) => {
        return contract.currency === Currency.CAD;
      }),
    );
  }

  public getPricingOptions(
    partnerId: string,
    subscriptionTierID: string,
    monthlyAddonPrice: number,
    yearlyAddonPrice: number,
    onboardingPrice: number,
  ): Observable<Map<Frequency, DialogPricingPlan>> {
    this.contract$ = this.getContract(partnerId);
    const yearlyAddon = yearlyAddonPrice * 100;
    const monthlyAddon = monthlyAddonPrice * 100;
    const prices$ = this.wholesalePricingService.getMultiWholesalePrices(partnerId, [subscriptionTierID]);
    const plans$: Observable<DialogPricingPlan[]> = prices$.pipe(
      map((prices) => {
        const planPrices = prices
          .filter((p) => p.sku === subscriptionTierID)
          .map((p) => {
            return p.prices.map((pp) => {
              const obp = onboardingPrice;
              const dpp: DialogPricingPlan = {
                name: pp.frequency === Frequency.Monthly ? 'monthly' : 'yearly',
                caption: pp.frequency === Frequency.Monthly ? 'Monthly plan' : 'Yearly plan',
                monthlyPrice:
                  pp.frequency === Frequency.Monthly
                    ? pp.pricingRules[0]?.price + monthlyAddon
                    : pp.pricingRules[0]?.price / 12 + yearlyAddon / 12,
                price: pp.pricingRules[0]?.price + yearlyAddon,
                currency: pp.currency,
                frequency: pp.frequency,
                totalCost:
                  pp.pricingRules[0]?.price +
                  (pp.frequency === Frequency.Monthly ? obp * 100 : 0) +
                  (pp.frequency === Frequency.Monthly ? monthlyAddon : yearlyAddon),
                onboardingPrice: obp,
              };
              return dpp;
            });
          });
        return planPrices[0];
      }),
    );

    return plans$.pipe(
      map((plansFromBilling) => {
        const entries = [];
        plansFromBilling.forEach((p) => {
          entries.push([p.frequency, p]);
        });
        return new Map<Frequency, DialogPricingPlan>(entries);
      }),
    );
  }

  purchaseSubscription(
    partnerId: string,
    tierId: string,
    selectedPricingPlan: Frequency,
    type: SubscriptionTierUpdateType,
  ): Observable<boolean> {
    this.purchasing$$.next(true);
    let freq = BillingFrequency.BILLING_FREQUENCY_MONTHLY;
    if (selectedPricingPlan === Frequency.Yearly) {
      freq = BillingFrequency.BILLING_FREQUENCY_YEARLY;
    }
    return this.partnerSubscriptionService.updateSubscriptionTier(partnerId, tierId, freq, type).pipe(
      catchError((err) => {
        this.purchasing$$.next(false);
        return throwError(err);
      }),
    );
  }

  public waitForSubscriptionChange(pid: string): Observable<WaitForSubscriptionChangeResult> {
    return this.partnerSubscriptionService.getUpdateSubscriptionTierStatus(pid).pipe(
      map((tierChangeStatus) => {
        if (tierChangeStatus.status === UpdateSubscriptionTierStatus.UPDATE_SUBSCRIPTION_TIER_STATUS_SUCCESS) {
          // success.  Stop retrying.
          return { success: true };
        }
        if (tierChangeStatus.status === UpdateSubscriptionTierStatus.UPDATE_SUBSCRIPTION_TIER_STATUS_FAILURE) {
          // failed. Stop retrying.
          return { success: false, reason: tierChangeStatus.reason };
        }
        // throw error to poll again
        throw false;
      }),
      retryWhen((err) => err.pipe(delay(1000))),
    );
  }

  private getContract(partnerId: string): Observable<Contract> {
    return this.billingService.getMultiContract([partnerId]).pipe(map((contracts) => contracts[partnerId]));
  }

  updateTos(): Observable<any> {
    return this.tosService.acceptTermsOfService();
  }
}
